import React from "react";
import { Card, CardBody, Container, Row, Col, Table } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
} from "chart.js";
import AnalyticsNavigation from "./components/AnalyticsNavigation";
import config from "../../config";
import { fetchData as apiFetchData } from "../../services/apiServices";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Simple debounce function to prevent multiple rapid API calls
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const EventsAndMarketsAnalytics = () => {
  const [analyticsData, setAnalyticsData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // API endpoint configuration - use the global config API_URL
  const [apiEndpoint, setApiEndpoint] = React.useState(
    `${config.API_URL}/api/admin/analytics/events-metrics`
  );

  // Debug mode state
  const [debugMode, setDebugMode] = React.useState(false);

  // Auto-refresh flag to control when API settings should trigger new requests
  const [autoRefresh, setAutoRefresh] = React.useState(true);

  // Mock data toggle
  const [useMockData, setUseMockData] = React.useState(false);

  // API method selection
  const [apiMethod, setApiMethod] = React.useState("fetch"); // 'fetch' or 'apiService'

  // Reference to store the previous API endpoint value
  const prevEndpointRef = React.useRef(apiEndpoint);

  // Reference to store the previous API method value
  const prevApiMethodRef = React.useRef(apiMethod);

  // Reference to store the previous mock data toggle value
  const prevMockDataRef = React.useRef(useMockData);

  // Mock data for testing when API is unavailable
  const mockData = {
    totalEvents: 199,
    activeEvents: 199,
    upcomingEvents: 0,
    closingEventsCount: 0,
    totalMarkets: 337,
    marketsByStatus: {
      open: 325,
      resolved: 0,
      settled: 11,
      disputed: 1,
    },
  };

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (debugMode) {
        console.log("fetchData called with settings:", {
          apiEndpoint,
          apiMethod,
          useMockData,
        });
      }

      // Use mock data if enabled
      if (useMockData) {
        if (debugMode) {
          console.log("Using mock data instead of API call");
        }
        // Use timeout to simulate API call
        setTimeout(() => {
          setAnalyticsData(mockData);
          setLoading(false);
        }, 500);
        return;
      }

      if (apiMethod === "apiService") {
        // Use the app's existing API service that works for other endpoints
        try {
          if (debugMode) {
            console.log(
              "Making API request using apiService to analytics/events-metrics"
            );
          }

          // The endpoint should be relative for apiServices
          const endpoint = "api/admin/analytics/events-metrics";
          const response = await apiFetchData(endpoint);

          if (debugMode) {
            console.log("API response:", response);
          }

          if (response.success) {
            setAnalyticsData(response.data);
          } else {
            throw new Error(
              response.error || "API returned unsuccessful response"
            );
          }
        } catch (e) {
          console.error("API service error details:", e);
          throw e;
        }
      } else {
        // Use direct fetch approach
        try {
          if (debugMode) {
            console.log("Making API request to:", apiEndpoint);
          }

          // Get auth token the same way other API calls do
          const token =
            localStorage.getItem("authUser") ||
            localStorage.getItem("authToken");

          // Create axios-compatible headers
          const headers = {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          };

          // Use direct fetch approach
          const response = await fetch(apiEndpoint, {
            method: "GET",
            headers: headers,
            mode: "cors", // Explicitly set CORS mode
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("API response error:", response.status, errorText);
            throw new Error(
              `API error: ${response.status} ${response.statusText}`
            );
          }

          const responseData = await response.json();

          if (debugMode) {
            console.log("API response:", responseData);
          }

          if (responseData.success) {
            setAnalyticsData(responseData.data);
          } else {
            throw new Error(
              responseData.message || "API returned unsuccessful response"
            );
          }
        } catch (e) {
          console.error("Fetch error details:", e);

          if (e.message.includes("Failed to fetch") || e.name === "TypeError") {
            throw new Error(
              `Network error connecting to API. Please check the server URL configuration (currently: ${apiEndpoint}).`
            );
          } else {
            throw e;
          }
        }
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching events and markets analytics data:", err);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, apiMethod, useMockData, debugMode]); // Include all dependencies that are used in the function

  // Handle retrying
  const handleRetry = () => {
    // Force a new data fetch
    fetchData();
  };

  // Main data fetching effect
  React.useEffect(() => {
    // Only fetch data on initial load and when retryCount changes
    fetchData();
  }, [fetchData]);

  // Effect to detect changes in API settings and trigger a fetch if needed
  React.useEffect(() => {
    // Check if any API-related settings have changed
    const endpointChanged = prevEndpointRef.current !== apiEndpoint;
    const apiMethodChanged = prevApiMethodRef.current !== apiMethod;
    const mockDataChanged = prevMockDataRef.current !== useMockData;

    // Update refs to current values
    prevEndpointRef.current = apiEndpoint;
    prevApiMethodRef.current = apiMethod;
    prevMockDataRef.current = useMockData;

    // If this is not the first render and settings have changed, trigger a fetch
    if (endpointChanged || apiMethodChanged || mockDataChanged) {
      // If auto refresh is enabled, trigger a fetch
      if (autoRefresh) {
        console.log("API settings changed, triggering a fetch");
        // Use a short delay to prevent multiple fetches if multiple settings change at once
        setTimeout(() => {
          fetchData();
        }, 300);
      } else {
        console.log("API settings changed, but auto-refresh is disabled");
      }
    }
  }, [apiEndpoint, apiMethod, useMockData, autoRefresh, fetchData]);

  // Prepare event status chart data
  const eventStatusChartData = {
    labels: analyticsData ? ["Active", "Upcoming", "Closing"] : [],
    datasets: [
      {
        label: "Event Status",
        data: analyticsData
          ? [
              analyticsData.activeEvents,
              analyticsData.upcomingEvents,
              analyticsData.closingEventsCount,
            ]
          : [],
        backgroundColor: [
          "rgba(75, 192, 192, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ],
        borderColor: [
          "rgba(75, 192, 192, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // Prepare market status chart data
  const marketStatusChartData = {
    labels: analyticsData
      ? Object.keys(analyticsData.marketsByStatus).map(
          (status) => status.charAt(0).toUpperCase() + status.slice(1)
        )
      : [],
    datasets: [
      {
        label: "Market Status",
        data: analyticsData ? Object.values(analyticsData.marketsByStatus) : [],
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ],
        borderColor: [
          "rgba(54, 162, 235, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Analytics"
          breadcrumbItem="Events & Markets Analytics"
        />
        <AnalyticsNavigation activeTab="markets" />

        {loading && (
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <Card>
            <CardBody>
              <div className="alert alert-danger mb-0">
                <h5 className="alert-heading">Error loading data</h5>
                <p>{error}</p>
                <div className="mt-3">
                  <strong>Troubleshooting steps:</strong>
                  <ul className="mt-2 mb-0">
                    <li>Check if the analytics API server is running</li>
                    <li>Verify your authentication token is valid</li>
                    <li>
                      Check browser console for more detailed error messages
                    </li>
                    <li>Try refreshing the page</li>
                  </ul>
                </div>
                <div className="mt-3">
                  <button
                    className="btn btn-outline-primary me-2"
                    onClick={handleRetry}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading...
                      </>
                    ) : (
                      <>Try Again</>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setDebugMode(!debugMode)}
                  >
                    {debugMode ? "Hide Debug Options" : "Show Debug Options"}
                  </button>
                </div>

                {debugMode && (
                  <div className="mt-3 p-3 bg-light border rounded">
                    <h6>Debug Options</h6>
                    <div className="mb-3">
                      <label className="form-label">API Endpoint:</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={apiEndpoint}
                          onChange={(e) => setApiEndpoint(e.target.value)}
                          placeholder="Enter API endpoint URL"
                        />
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => window.open(apiEndpoint, "_blank")}
                          title="Open API in new tab"
                        >
                          Test
                        </button>
                      </div>
                      <small className="text-muted">
                        Try changing to your server's actual address if using
                        localhost
                      </small>
                    </div>
                    <div className="mb-3">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          localStorage.removeItem("authUser");
                          alert(
                            "Auth token cleared. You may need to log in again."
                          );
                          window.location.reload();
                        }}
                      >
                        Clear Auth Token
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          console.log(
                            "Current auth token:",
                            localStorage.getItem("authUser")
                          );
                          alert("Auth token logged to console");
                        }}
                      >
                        Log Token to Console
                      </button>
                    </div>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="useMockDataCheck"
                        checked={useMockData}
                        onChange={() => setUseMockData(!useMockData)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="useMockDataCheck"
                      >
                        Use Mock Data (bypasses API)
                      </label>
                      {useMockData && (
                        <div className="mt-2 alert alert-info py-2">
                          <small>
                            Using mock data. API calls are disabled.
                          </small>
                        </div>
                      )}
                    </div>

                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="autoRefreshCheck"
                        checked={autoRefresh}
                        onChange={() => setAutoRefresh(!autoRefresh)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="autoRefreshCheck"
                      >
                        Auto-refresh on settings change
                      </label>
                      {!autoRefresh && (
                        <div className="mt-2 alert alert-warning py-2">
                          <small>
                            Auto-refresh disabled. Click "Apply & Fetch" to
                            update data after changing settings.
                          </small>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label d-flex justify-content-between align-items-center">
                        <span>Quick API URL Fixes:</span>
                        <small className="text-muted">
                          Click to try alternative URLs
                        </small>
                      </label>
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setApiEndpoint(
                              `${config.API_URL}/api/admin/analytics/events-metrics`
                            );
                          }}
                        >
                          Use Config URL
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setApiEndpoint(
                              "http://65.0.135.240/api/admin/analytics/events-metrics"
                            );
                          }}
                        >
                          Use Production URL
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setApiEndpoint(
                              "/api/admin/analytics/events-metrics"
                            );
                          }}
                        >
                          Use Relative Path
                        </button>
                        <button
                          className="btn btn-primary btn-sm ms-2"
                          onClick={handleRetry}
                        >
                          Apply & Fetch
                        </button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">API Method:</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="apiMethod"
                            id="apiMethodFetch"
                            checked={apiMethod === "fetch"}
                            onChange={() => setApiMethod("fetch")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="apiMethodFetch"
                          >
                            Direct Fetch
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="apiMethod"
                            id="apiMethodApiService"
                            checked={apiMethod === "apiService"}
                            onChange={() => setApiMethod("apiService")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="apiMethodApiService"
                          >
                            App API Service
                          </label>
                        </div>
                      </div>
                      <small className="text-muted">
                        Use the App API Service to use the same method as other
                        working API calls
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {analyticsData && (
          <>
            <Row>
              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {analyticsData.totalEvents.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Events</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className="ri-calendar-event-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {analyticsData.totalMarkets.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Markets</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-success">
                          <i className="ri-store-2-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

             

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {analyticsData.marketsByStatus.open.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Open Markets</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-warning">
                          <i className="ri-exchange-dollar-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
               <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {analyticsData.activeEvents.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Active Events</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-info align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-info">
                          <i className="ri-calendar-check-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">
                      Event Status Distribution
                    </h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Pie
                        data={eventStatusChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">
                      Market Status Distribution
                    </h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Pie
                        data={marketStatusChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Market Status Summary</h4>
                    <div className="table-responsive">
                      <Table className="table-striped mb-0">
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th>Count</th>
                            <th>Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(analyticsData.marketsByStatus).map(
                            ([status, count]) => (
                              <tr key={status}>
                                <td>
                                  <span
                                    className={`badge bg-${
                                      status === "open"
                                        ? "success"
                                        : status === "resolved"
                                        ? "primary"
                                        : status === "settled"
                                        ? "info"
                                        : "warning"
                                    }`}
                                  >
                                    {status.charAt(0).toUpperCase() +
                                      status.slice(1)}
                                  </span>
                                </td>
                                <td>{count}</td>
                                <td>
                                  {(
                                    (count / analyticsData.totalMarkets) *
                                    100
                                  ).toFixed(2)}
                                  %
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Event Statistics</h4>
                    <div className="table-responsive">
                      <Table className="mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">Total Events</th>
                            <td>{analyticsData.totalEvents}</td>
                            <th scope="row">Active Markets</th>
                            <td>{analyticsData.marketsByStatus.open}</td>
                          </tr>
                          <tr>
                            <th scope="row">Upcoming Events</th>
                            <td>{analyticsData.upcomingEvents}</td>
                            <th scope="row">Closing Events</th>
                            <td>{analyticsData.closingEventsCount}</td>
                          </tr>
                          <tr>
                            <th scope="row">Total Markets</th>
                            <td>{analyticsData.totalMarkets}</td>
                            <th scope="row">Markets per Event</th>
                            <td>
                              {(
                                analyticsData.totalMarkets /
                                analyticsData.totalEvents
                              ).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default EventsAndMarketsAnalytics;
